export function generateLegalCSS(): string {
  return `

/* ============================================
   LEGAL PAGES
   ============================================ */

.legal-page {
  padding: var(--sp-20) 0;
}
.legal-page h1 {
  font-family: var(--font-display);
  font-size: var(--text-4xl);
  font-weight: 700;
  margin-bottom: var(--sp-8);
}
.legal-page h2 {
  font-size: var(--text-xl);
  font-weight: 600;
  margin-top: var(--sp-8);
  margin-bottom: var(--sp-4);
}
.legal-page p {
  color: var(--c-text-light);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--sp-4);
}
.legal-page a {
  color: var(--c-primary);
}

/* ============================================
   CONTENT PAGES (Angebot, Methoden, Über mich)
   ============================================ */

.content-page {
  padding-top: calc(var(--header-h, 44px) + var(--sp-8));
  padding-bottom: var(--density-section-py, var(--sp-16));
}
.content-page__hero {
  text-align: center;
  padding: var(--sp-16) 0 var(--sp-12);
}
.content-page__hero h1 {
  font-family: var(--font-display);
  font-size: clamp(var(--text-3xl), 5vw, var(--text-4xl));
  font-weight: var(--weight-bold, 700);
  line-height: var(--leading-snug, 1.3);
  margin-bottom: var(--sp-4);
}
.content-page__hero p {
  font-size: var(--text-lg);
  color: var(--c-text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: var(--leading-body, 1.6);
}

/* --- Pricing Grid (Angebot) — Layout: macro-3col-equal --- */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(var(--layout-columns-desktop), minmax(0, 1fr));
  gap: var(--layout-gap);
  padding: var(--sp-12) 0;
}
.pricing-card { grid-column: span 4; }
@media (max-width: 900px) and (min-width: 641px) {
  .pricing-grid { grid-template-columns: repeat(var(--layout-columns-tablet), minmax(0, 1fr)); }
  .pricing-card { grid-column: span 4; }
}
@media (max-width: 640px) {
  .pricing-grid { grid-template-columns: repeat(var(--layout-columns-mobile), minmax(0, 1fr)); }
  .pricing-card { grid-column: span 4; }
}
.pricing-card {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-xl, 16px);
  padding: var(--density-card-p, var(--sp-8));
  display: flex;
  flex-direction: column;
  transition: transform var(--duration-normal, 0.2s) var(--ease-out),
              box-shadow var(--duration-normal, 0.2s) var(--ease-out);
}
.pricing-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}
.pricing-card--highlighted {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 1px var(--c-primary);
  position: relative;
}
.pricing-card--highlighted::before {
  content: 'Empfohlen';
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--c-primary);
  color: var(--c-on-primary, #fff);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold, 600);
  padding: 4px 16px;
  border-radius: var(--radius-full, 9999px);
  letter-spacing: var(--tracking-wide, 0.04em);
}
.pricing-card__name {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-bold, 700);
}
.pricing-card__price {
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold, 700);
  color: var(--c-primary);
  margin: var(--sp-2) 0;
}
.pricing-card__description {
  color: var(--c-text-light);
  margin-bottom: var(--sp-6);
  font-size: var(--text-sm);
}
.pricing-card__features {
  list-style: none;
  padding: 0;
  margin: 0 0 var(--sp-8);
  flex-grow: 1;
}
.pricing-card__features li {
  padding: var(--sp-2) 0;
  border-bottom: 1px solid var(--c-border);
  font-size: var(--text-sm);
}
.pricing-card__features li::before {
  content: '\u2713 ';
  color: var(--c-primary);
  font-weight: var(--weight-bold, 700);
}

/* --- Method Steps (Methoden) --- */
.method-steps {
  display: grid;
  gap: var(--sp-10);
  padding: var(--sp-12) 0;
}
.method-step {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--sp-6);
  align-items: start;
}
.method-step__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-2xl);
  background: rgba(var(--c-primary-rgb) / 0.08);
  border-radius: var(--radius-lg, 12px);
  flex-shrink: 0;
}
.method-step__content h3 {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-semibold, 600);
  margin-bottom: var(--sp-2);
}
.method-step__content p {
  color: var(--c-text-light);
  line-height: var(--leading-body, 1.6);
  max-width: 55ch;
}

/* --- Tools Grid (Methoden) --- */
.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-6);
  padding: var(--sp-4) 0;
}
.tool-card {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg, 12px);
  padding: var(--sp-6);
  text-align: center;
  transition: box-shadow var(--duration-normal, 0.2s) var(--ease-out);
}
.tool-card:hover {
  box-shadow: var(--shadow-md);
}
.tool-card__name {
  font-weight: var(--weight-semibold, 600);
  margin-bottom: var(--sp-1);
}
.tool-card__desc {
  font-size: var(--text-sm);
  color: var(--c-text-light);
}

/* --- Timeline (Über mich) --- */
.timeline {
  position: relative;
  padding: var(--sp-8) 0;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 24px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--c-border);
}
.timeline__item {
  position: relative;
  padding-left: 64px;
  padding-bottom: var(--sp-8);
}
.timeline__year {
  position: absolute;
  left: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--c-primary);
  color: var(--c-on-primary, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xs);
  font-weight: var(--weight-bold, 700);
  z-index: 1;
}
.timeline__content h3 {
  font-family: var(--font-display);
  font-weight: var(--weight-semibold, 600);
  margin-bottom: var(--sp-1);
}
.timeline__content p {
  color: var(--c-text-light);
  line-height: var(--leading-body, 1.6);
}

/* --- Values Grid (Über mich) --- */
.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--sp-6);
  padding: var(--sp-4) 0;
}
.value-card {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg, 12px);
  padding: var(--sp-6);
  transition: box-shadow var(--duration-normal, 0.2s) var(--ease-out);
}
.value-card:hover {
  box-shadow: var(--shadow-md);
}
.value-card h3 {
  font-family: var(--font-display);
  font-weight: var(--weight-semibold, 600);
  margin-bottom: var(--sp-2);
}
.value-card p {
  color: var(--c-text-light);
  font-size: var(--text-sm);
  line-height: var(--leading-body, 1.6);
}

/* --- Certifications (Über mich) --- */
.certifications {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  padding: var(--sp-4) 0;
}
.certification-badge {
  background: rgba(var(--c-primary-rgb) / 0.08);
  border: 1px solid var(--c-primary);
  border-radius: var(--radius-full, 9999px);
  padding: var(--sp-2) var(--sp-4);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold, 600);
  color: var(--c-primary);
}

/* --- About Hero (Über mich with image) — Layout: macro-2col-6-6 --- */
.about-hero {
  display: grid;
  grid-template-columns: repeat(var(--layout-columns-desktop), minmax(0, 1fr));
  gap: var(--layout-gap);
  align-items: center;
  padding: var(--sp-16) 0;
}
.about-hero__text { grid-column: span 6; min-width: 0; }
.about-hero__image { grid-column: span 6; min-width: 0; }
@media (max-width: 900px) and (min-width: 641px) {
  .about-hero { grid-template-columns: repeat(var(--layout-columns-tablet), minmax(0, 1fr)); }
  .about-hero__text, .about-hero__image { grid-column: span 8; }
}
@media (max-width: 640px) {
  .about-hero { grid-template-columns: repeat(var(--layout-columns-mobile), minmax(0, 1fr)); }
  .about-hero__text, .about-hero__image { grid-column: span 4; }
}
.about-hero__text h1 {
  font-family: var(--font-display);
  font-size: clamp(var(--text-3xl), 5vw, var(--text-4xl));
  font-weight: var(--weight-bold, 700);
  line-height: var(--leading-snug, 1.3);
  margin-bottom: var(--sp-6);
}
.about-hero__image img {
  width: 100%;
  border-radius: var(--radius-xl, 16px);
  object-fit: cover;
  aspect-ratio: 4/5;
}

/* --- Content Section (Shared) --- */
.content-section {
  padding: var(--sp-10) 0;
}
.content-section__title {
  font-family: var(--font-display);
  font-size: clamp(var(--text-xl), 3vw, var(--text-2xl));
  font-weight: var(--weight-bold, 700);
  margin-bottom: var(--sp-6);
}
.content-section__text {
  color: var(--c-text-light);
  line-height: var(--leading-body, 1.6);
  max-width: 65ch;
}

/* --- Page CTA (Shared) --- */
.page-cta {
  text-align: center;
  padding: var(--density-section-py, var(--sp-16));
  background: rgba(var(--c-primary-rgb) / 0.04);
  border-radius: var(--radius-xl, 16px);
  margin: var(--sp-12) 0;
}
.page-cta h2 {
  font-family: var(--font-display);
  font-size: clamp(var(--text-xl), 3vw, var(--text-2xl));
  font-weight: var(--weight-bold, 700);
  margin-bottom: var(--sp-4);
}
.page-cta p {
  color: var(--c-text-light);
  margin-bottom: var(--sp-6);
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: var(--leading-body, 1.6);
}

/* --- Content Pages Responsive --- */
@media (max-width: 900px) {
  .about-hero__image {
    order: -1;
  }
  .about-hero__image img {
    aspect-ratio: 16/9;
  }
}
@media (max-width: 640px) {
  .method-step {
    grid-template-columns: 1fr;
  }
  .method-step__icon {
    width: 44px;
    height: 44px;
    font-size: var(--text-lg);
  }
  .timeline::before {
    left: 18px;
  }
  .timeline__item {
    padding-left: 52px;
  }
  .timeline__year {
    width: 36px;
    height: 36px;
    font-size: 10px;
  }
}

`;
}
